import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import quarkusApi from '../api/quarkusApi';
import { readCache, writeCache } from '../api/offlineCache/db';

// Hook genérico de leitura com cache offline (Nível 1: só leitura).
//
// Fluxo:
//  - Online: busca do backend, mostra o dado fresco e o grava no IndexedDB.
//  - Falha de rede/backend: cai para a última resposta salva no IndexedDB
//    (se houver) e marca isStale = true, expondo updatedAt para a UI avisar
//    que o dado está desatualizado.
//  - 401: redireciona para /expired-session (cache não resolve auth).
//  - Sem cache e sem rede: expõe error para o componente exibir.
//
// A cacheKey padrão é a própria url (endpoints diferentes = entradas
// diferentes). url falsy desativa a busca.
export function useCachedQuery(url, options = {}) {
  const { cacheKey = url, enabled = true } = options;
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [isStale, setStale] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [error, setError] = useState(null);

  // Evita setState após desmontar ou após a url mudar no meio da requisição.
  const requestIdRef = useRef(0);

  const fetchData = useCallback(() => {
    if (!enabled || !url) return;

    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestId === requestIdRef.current;

    setLoading(true);
    quarkusApi
      .get(url, { headers: { Authorization: sessionStorage.getItem('token') } })
      .then((returnFromApi) => {
        if (!isCurrent()) return;
        setData(returnFromApi.data);
        setStale(false);
        setUpdatedAt(Date.now());
        setError(null);
        writeCache(cacheKey, returnFromApi.data).catch(() => {
          /* cache é best-effort; falha ao gravar não deve quebrar a tela */
        });
      })
      .catch((returnFromApi) => {
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
          return;
        }
        // Sem resposta do backend (offline/timeout): tenta servir do cache.
        readCache(cacheKey)
          .then((cached) => {
            if (!isCurrent()) return;
            if (cached) {
              setData(cached.data);
              setStale(true);
              setUpdatedAt(cached.updatedAt);
              setError(null);
            } else {
              setError(returnFromApi.response?.data);
            }
          })
          .catch(() => {
            if (!isCurrent()) return;
            setError(returnFromApi.response?.data);
          });
      })
      .finally(() => {
        if (isCurrent()) setLoading(false);
      });
  }, [url, cacheKey, enabled, navigate]);

  useEffect(() => {
    fetchData();
    return () => {
      // Invalida a requisição em voo ao desmontar / trocar de url.
      requestIdRef.current++;
    };
  }, [fetchData]);

  return { data, isLoading, isStale, updatedAt, error, refetch: fetchData };
}
